import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";

import {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_META_IMAGE,
  DEFAULT_META_KEYWORDS,
  DEFAULT_META_TITLE,
  SHOP_DEPARTMENTS,
  SHOP_DEPARTMENTS_TEXT,
  SITE_NAME,
  SITE_URL,
} from "../constants/brand";

const DEFAULT_TITLE = DEFAULT_META_TITLE;
const DEFAULT_DESC = DEFAULT_META_DESCRIPTION;
const DEFAULT_KEYWORDS = DEFAULT_META_KEYWORDS;
const DEFAULT_IMAGE = DEFAULT_META_IMAGE;

const toAbsUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (/^(https?|data|blob):/i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const toCanonicalUrl = (url) => {
  const absoluteUrl = toAbsUrl(url) || SITE_URL;

  try {
    const parsed = new URL(absoluteUrl);
    const site = new URL(SITE_URL);
    const pathname = parsed.pathname.replace(/\/{2,}/g, "/") || "/";
    const canonicalPath =
      pathname === "/" ? "/" : `${pathname.replace(/\/+$/, "")}/`;

    return `${site.origin}${canonicalPath}`;
  } catch {
    return SITE_URL;
  }
};

const normalizeImageValue = (image) => {
  if (!image) return "";
  if (typeof image === "string") return toAbsUrl(image);
  if (typeof image === "object") {
    return toAbsUrl(image.url || image.src || image.secure_url || "");
  }
  return "";
};

const cleanMetaText = (value = "", maxLength = 160) => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!text || text.length <= maxLength) return text;

  const sliced = text.slice(0, maxLength - 1).trimEnd();
  const lastSpace = sliced.lastIndexOf(" ");

  return `${sliced.slice(0, lastSpace > 80 ? lastSpace : sliced.length)}…`;
};

const toOfferAvailability = (product = {}) => {
  if (product.availabilityUrl) return product.availabilityUrl;
  if (product.availability === "https://schema.org/PreOrder") {
    return product.availability;
  }

  const numericAvailability = Number(
    product?.availability ??
      product?.stock ??
      product?.available ??
      product?.catalogAvailable ??
      0,
  );

  return numericAvailability > 0
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock";
};

export default function Meta({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESC,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  url = SITE_URL,
  robots = "index, follow, max-image-preview:large",
  products = [],
  product = null,
  breadcrumbs = null,
}) {
  const canonicalUrl = toCanonicalUrl(url);
  const resolvedImage = normalizeImageValue(image) || DEFAULT_IMAGE;

  const schemaMarkup = useMemo(() => {
    const schemas = [];

    schemas.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: DEFAULT_IMAGE,
      description: DEFAULT_DESC,
      department: SHOP_DEPARTMENTS.map((shopName) => ({
        "@type": "Store",
        name: `${SITE_NAME} - ${shopName}`,
        url: SITE_URL,
      })),
    });

    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });

    if (Array.isArray(products) && products.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: products.map((p, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: toAbsUrl(
            p?.url ||
              (p?.shopKey && (p?.slug || p?._id)
                ? `/shop/${p.shopKey}/product/${p.slug || p._id}`
                : `/product/${p.slug || p._id}`),
          ),
          name: p?.name || "",
        })),
      });
    }

    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs
          .filter((b) => b?.name && b?.url)
          .map((b, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: b.name,
            item: toAbsUrl(b.url),
          })),
      });
    }

    if (product?.name) {
      const price = Number(product?.price);
      const images = Array.isArray(product?.images)
        ? product.images.map(normalizeImageValue).filter(Boolean)
        : product?.image
          ? [normalizeImageValue(product.image)].filter(Boolean)
          : [resolvedImage].filter(Boolean);

      const offer = {
        "@type": "Offer",
        priceCurrency: product?.currency || "GBP",
        price: Number.isFinite(price) ? price.toFixed(2) : undefined,
        availability: toOfferAvailability(product),
        url: toAbsUrl(product?.url || canonicalUrl),
        itemCondition: "https://schema.org/NewCondition",
        seller: {
          "@type": "Organization",
          name: SITE_NAME,
        },
      };

      schemas.push({
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: cleanMetaText(product.description || description, 260),
        image: images,
        sku: product.sku || undefined,
        mpn: product.mpn || product.productCode || product.sku || undefined,
        brand: {
          "@type": "Brand",
          name: product?.brand || SITE_NAME,
        },
        category:
          product?.category || product?.shopName || SHOP_DEPARTMENTS_TEXT,
        color: product?.color || product?.colour || undefined,
        material: product?.material || undefined,
        size: product?.size || undefined,
        url: toAbsUrl(product?.url || canonicalUrl),
        offers: offer,
      });
    }

    return schemas.length ? schemas : null;
  }, [
    products,
    breadcrumbs,
    product,
    description,
    resolvedImage,
    canonicalUrl,
  ]);

  const isProductPage = Boolean(product?.name);

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta
        property="og:type"
        content={isProductPage ? "product" : "website"}
      />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedImage} />
      <meta name="twitter:image:alt" content={title} />

      {isProductPage && Number.isFinite(Number(product?.price)) ? (
        <meta property="product:price:amount" content={Number(product.price).toFixed(2)} />
      ) : null}
      {isProductPage ? (
        <meta property="product:price:currency" content={product?.currency || "GBP"} />
      ) : null}

      {schemaMarkup?.map((schema, idx) => (
        <script
          key={`ld-${idx}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
}
